import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getLedgerEntries } from '../services/ledger';
import { getFileOpeningById } from '../services/fileOpenings';
import toast from 'react-hot-toast';
import { PDFViewer } from '@react-pdf/renderer';
import FileOpeningLedgerPdf from '../components/pdf/FileOpeningLedgerPdf';
import FileOpeningLedgerPdfButton from '../components/pdf/FileOpeningLedgerPdfButton';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const FileOpeningLedger = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [fileOpening, setFileOpening] = useState(null);
    const [loading, setLoading] = useState(true);
    const [limit,] = useState(100); // Fetch 100 by default, maybe more?
    const [currentPage, setCurrentPage] = useState(1);
    const [, setTotal] = useState(0);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    const getDataToSubmit = () => {
        return {
            fileOpening,
            entries
        };
    }

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            try {
                const [fileResponse, ledgerResponse] = await Promise.all([
                    getFileOpeningById(id),
                    // Fetch ALL entries to calculate running balance correctly
                    // or at least a very large number. 
                    // For a true ledger, we need chronological order.
                    getLedgerEntries(id, { limit: 1000, offset: 0, sortBy: 'date', sortOrder: 'asc' })
                ]);

                setFileOpening(fileResponse);

                // Calculate running balance
                let runningBalance = 0;
                const entriesWithBalance = ledgerResponse.entries.map(entry => {
                    runningBalance += parseFloat(entry.amount || 0);
                    return { ...entry, balance: runningBalance };
                });

                setEntries(entriesWithBalance);
                setTotal(ledgerResponse.count);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    if (getDataToSubmit().fileOpening == null) return false;

    console.log(getDataToSubmit());

    // Client-side pagination because we fetched everything for balance calculation
    // const totalPages = Math.ceil(entries.length / limit); // Handled by Pagination component
    const paginatedEntries = entries.slice((currentPage - 1) * limit, currentPage * limit);

    const getClientName = (file) => {
        if (!file) return '';
        return `${file.title || ''} ${file.forename} ${file.surname}`.trim();
    };

    return (
        <DashboardLayout pageTitle={`Ledger - File #${id}`}>
            <div className='flex justify-between'>
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                        {getClientName(fileOpening)}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        File Ref: ska-{id}
                    </p>
                </div>
                <div className="mb-6">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowPdfPreview(!showPdfPreview)}
                        className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                    >
                        {showPdfPreview ? 'Hide PDF Preview' : 'Show PDF Preview'}
                    </button>
                    <FileOpeningLedgerPdfButton data={getDataToSubmit()} />
                </div>
            </div>

            <div className='mb-4'>
                {showPdfPreview && getDataToSubmit() && (
                    <div style={{ height: '80vh' }} className="mb-4 text-right">
                        <PDFViewer width="100%" height="100%">
                            <FileOpeningLedgerPdf data={getDataToSubmit()} />
                        </PDFViewer>
                    </div>
                )}
            </div>

            <TableContainer>
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">Date</th>
                                        <th scope="col" className="px-6 py-3">Type</th>
                                        <th scope="col" className="px-6 py-3">Reference</th>
                                        <th scope="col" className="px-6 py-3">Description</th>
                                        <th scope="col" className="px-6 py-3 text-right">Debit (Inv)</th>
                                        <th scope="col" className="px-6 py-3 text-right">Credit (Rec)</th>
                                        <th scope="col" className="px-6 py-3 text-right">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedEntries.map((entry) => (
                                        <tr key={`${entry.type}-${entry.id}`} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {entry.date}
                                            </td>
                                            <td className="px-6 py-4 capitalize">
                                                {entry.type}
                                            </td>
                                            <td className="px-6 py-4">
                                                {entry.type === 'invoice' ? (
                                                    <Link to={`/file-opening/${id}/invoice/${entry.id}`} className="text-blue-600 hover:underline">
                                                        {entry.reference || `#${entry.id}`}
                                                    </Link>
                                                ) : (
                                                    <Link to={`/file-opening/${id}/receipt/${entry.id}`} className="text-blue-600 hover:underline">
                                                        {entry.reference || `#${entry.id}`}
                                                    </Link>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {entry.description || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right font-medium">
                                                {parseFloat(entry.debit) !== 0 ? `£${parseFloat(entry.debit).toFixed(2)}` : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right font-medium text-green-600 dark:text-green-400">
                                                {parseFloat(entry.credit) !== 0 ? `£${parseFloat(entry.credit).toFixed(2)}` : '-'}
                                            </td>
                                            <td className={`px-6 py-4 text-right font-bold ${entry.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                £{entry.balance_amount.toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                    {paginatedEntries.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                                No ledger entries found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            total={entries.length}
                            limit={limit}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </TableContainer>
        </DashboardLayout>
    );
};

export default FileOpeningLedger;
